import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Typography,
  Box,
  Button,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { endLoading, startLoading } from '../../../Redux/loadingSlice';
import Axios from '../../../config/axios';
import { UrlConstant } from '../../../commom/UrlConstant';
import { renderPaymentIcon } from '../../../utils/utils';
import { usePriceFormatter } from '../../../hooks';
import { useAppDispatch } from '../../../Redux/hooks';
import { trackEvent } from '../../../config/firebase';
import { Constant } from '../../../commom/Constants';

interface BoosterLoanPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  boosterLoanDetails: any;
  paymentMethods: any[];
  selectedPaymentMethodId: string | null;
  onPaymentMethodChange: (methodId: string) => void;
  onPaymentSuccess: (message: string, installmentIds: string[], installmentNos?: number[]) => void;
  onPaymentError: (message: string) => void;
  onRefresh: () => void;
  setShowPaymentMethodDialog: (show: boolean) => void;
}

const BoosterLoanPaymentDialog: React.FC<BoosterLoanPaymentDialogProps> = ({
  open,
  onClose,
  boosterLoanDetails,
  paymentMethods,
  selectedPaymentMethodId,
  onPaymentMethodChange,
  onPaymentSuccess,
  onPaymentError,
  onRefresh,
  setShowPaymentMethodDialog,
}) => {
  const dispatch = useAppDispatch();
  const [selectedInstallments, setSelectedInstallments] = useState(1);
  const [isFullRepayment, setIsFullRepayment] = useState(false);
  const [payableAmount, setPayableAmount] = useState('0.00');
  const [remainingAmount, setRemainingAmount] = useState<number | null>(null);
  const [savingAmount, setSavingAmount] = useState<number | null>(null);
  const [isLoadingAmount, setIsLoadingAmount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState<{
    principle: number | null;
    interestFees: number | null;
    originationFee: number | null;
    prepaymentFee: number | null;
  }>({
    principle: null,
    interestFees: null,
    originationFee: null,
    prepaymentFee: null,
  });

  // Calculate remaining payable installments
  const remainingPayableInstallments =
    (boosterLoanDetails?.remaining_installment || 0) -
    (boosterLoanDetails?.installments?.filter((i: any) => i.status === 'InProgress')?.length || 0);

  const formatter = usePriceFormatter()

  const method = paymentMethods?.find((m: any) => m.id === selectedPaymentMethodId);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedInstallments(1);
      setIsFullRepayment(false);
      setPayableAmount('0.00');
      setRemainingAmount(null);
      setSavingAmount(null);
      setIsLoadingAmount(false);
      setPaymentBreakdown({
        principle: null,
        interestFees: null,
        originationFee: null,
        prepaymentFee: null,
      });
    }
  }, [open]);

  // Function to fetch prepayment amount
  const fetchPrepaymentAmount = useCallback(async () => {
    if (!boosterLoanDetails?.loanDetail?.id) {
      console.error('Invalid loan data');
      return false;
    }

    setIsLoadingAmount(true);
    try {
      const response = await Axios.get(
        UrlConstant.Crypto.GetCryptoPrepaymentAmount + `?loanId=${boosterLoanDetails.loanDetail.id}`
      );

      if (response.data?.data?.success) {
        const data = response.data.data;
        setPayableAmount(data.payableAmount);
        setRemainingAmount(data.remainingAmount || null);
        setSavingAmount(data.savingAmount || null);

        // Set payment breakdown
        setPaymentBreakdown({
          principle: data.Principle || null,
          interestFees: data.InterestFees || null,
          originationFee: (data.originationFeePercentageAmount || data.originationFeeDollar) || null,
          prepaymentFee: (data.prepaymentPercentageAmount || data.prepaymentDollarAmount) || null,
        });

        return true;
      } else {
        onPaymentError('Failed to get payable amount');
        return false;
      }
    } catch (error: any) {
      console.error('Error fetching payable amount:', error);
      onPaymentError('Failed to get payable amount');
      return false;
    } finally {
      setIsLoadingAmount(false);
    }
  }, [boosterLoanDetails?.loanDetail?.id, onPaymentError]);

  // Auto-switch to full repayment when selected installments match remaining installments
  useEffect(() => {
    if (open && !isFullRepayment && selectedInstallments === remainingPayableInstallments && remainingPayableInstallments > 0) {
      setIsFullRepayment(true);
      fetchPrepaymentAmount();
    }
  }, [selectedInstallments, remainingPayableInstallments, open, isFullRepayment, fetchPrepaymentAmount]);

  const handleSwitch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setIsFullRepayment(isChecked);

    if (isChecked) {
      // Fetch payable amount when switch is turned on
      const success = await fetchPrepaymentAmount();
      if (!success) {
        setIsFullRepayment(false);
      }
    } else {
      setPayableAmount('0.00');
      setRemainingAmount(null);
      setSavingAmount(null);
      setSelectedInstallments(1);
      setPaymentBreakdown({
        principle: null,
        interestFees: null,
        originationFee: null,
        prepaymentFee: null,
      });
    }
  };

  const processFullPayment = async () => {
    if (!boosterLoanDetails?.loanDetail?.id || !selectedPaymentMethodId) {
      onPaymentError('Please select a payment method');
      return;
    }

    setLoading(true);
    dispatch(startLoading());

    try {
      const payload = {
        loanId: boosterLoanDetails.loanDetail.id,
        preferredAutopayProfileId: selectedPaymentMethodId,
        totalRepaymentAmount: parseFloat(payableAmount),
        totalRemainingQuantity: boosterLoanDetails?.loanDetail?.remaining_stock_quantity,
        paymentMethodType: method?.type
      };

      const response = await Axios.post(UrlConstant.Crypto.PrePayCryptoLoan, payload);
      if (response.status >= 200 && response.status < 300) {
        // Get all remaining unpaid installment IDs
        if (Constant.ENV === "production") {
          trackEvent("booster_loan_completed", {
            source: `Type: ${method?.type},
          LoanId: ${boosterLoanDetails.loanDetail.id},
          Amount: ${payableAmount}`
          });
        }

        const unpaidInstallments = boosterLoanDetails?.installments?.filter(
          (inst: any) => inst.status?.toUpperCase() !== 'PAID'
        ) || [];
        const installmentIds = unpaidInstallments.map((inst: any) => inst.id);
        const installmentNos = unpaidInstallments.map((inst: any) => inst.installment_no);

        onClose();
        onPaymentSuccess('Full payment processed successfully!', installmentIds, installmentNos);
      } else {
        onPaymentError(response.data?.message || 'Payment failed');
      }
    } catch (error: any) {
      console.error('Error processing full payment:', error);
      onPaymentError(error.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
      dispatch(endLoading());
    }
  };

  const processInstallmentPayment = async () => {
    setLoading(true);
    dispatch(startLoading());
    try {
      const payload = {
        loanId: boosterLoanDetails?.loanDetail?.id,
        totalInstallment: selectedInstallments,
        preferredAutopayProfileId: selectedPaymentMethodId,
        totalRepaymentAmount: isFullRepayment ? payableAmount : (boosterLoanDetails?.installments[0]?.amount * selectedInstallments),
        totalRemainingQuantity: boosterLoanDetails?.loanDetail?.remaining_stock_quantity,
        paymentMethodType: method?.type
      };
      const response = await Axios.post(UrlConstant.BoosterLoan.BOOSTER_LOAN_MANUAL_PAY_LOAN, payload);
      if (response.data?.status) {
        // Extract installment numbers from response
        let installmentNos = response.data?.installmentNo || [];

        // Find installment IDs that match the installment numbers from response
        const installmentIds: string[] = [];
        if (installmentNos.length > 0) {
          boosterLoanDetails?.installments?.forEach((inst: any) => {
            if (installmentNos.includes(inst.installment_no)) {
              installmentIds.push(inst.id);
            }
          });
        }

        // Fallback: if no matching installments found, use the old logic
        if (installmentIds.length === 0) {
          const unpaidInstallments = boosterLoanDetails?.installments?.filter(
            (inst: any) => inst.status?.toUpperCase() !== 'PAID' && inst.status?.toUpperCase() !== 'INPROGRESS'
          ) || [];
          const sortedUnpaid = unpaidInstallments.sort((a: any, b: any) =>
            (a.installment_no || 0) - (b.installment_no || 0)
          );
          const installmentsToPay = sortedUnpaid.slice(0, selectedInstallments);
          installmentIds.push(...installmentsToPay.map((inst: any) => inst.id));
          if (!installmentNos.length) {
            installmentNos = installmentsToPay.map((inst: any) => inst.installment_no);
          }
        }

        onClose();
        onPaymentSuccess('Payment successful!', installmentIds, installmentNos);
      } else {
        onPaymentError(response.data?.message || 'Payment failed.');
      }
    } catch (error: any) {
      onPaymentError(error?.response?.data?.message || 'Payment failed.');
      console.error('Manual pay loan error:', error);
    } finally {
      setLoading(false);
      dispatch(endLoading());
    }
  };

  const handlePayNow = async () => {
    if (isFullRepayment) {
      await processFullPayment();
    } else {
      await processInstallmentPayment();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
          color: 'white',
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white', fontWeight: 700, fontSize: 20 }}>
        Pay Remaining Installments
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Full Repayment Toggle Section */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              py: 2,
              px: 2.5,
              borderRadius: '12px',
              bgcolor: isFullRepayment ? 'rgba(74, 122, 242, 0.08)' : 'transparent',
              border: isFullRepayment ? '1px solid rgba(74, 122, 242, 0.2)' : '1px solid rgba(255,255,255,0.08)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: isFullRepayment ? 'rgba(74, 122, 242, 0.12)' : 'rgba(255,255,255,0.03)',
                borderColor: isFullRepayment ? 'rgba(74, 122, 242, 0.3)' : 'rgba(255,255,255,0.12)'
              }
            }}
            onClick={() => handleSwitch({ target: { checked: !isFullRepayment } } as any)}
          >
            <Box display="flex" gap="12px" alignItems="center">
              <Box sx={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                bgcolor: 'rgba(238, 164, 21, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 19.667L10.8082 15.0446H7.9985L11.1254 10.6942H7.6813L12.3489 4.66699H16.2972L12.9381 8.42829L16.175 8.33769L12.1543 13.232H15.3399L7.5 19.667Z" fill="#EEA415" />
                </svg>
              </Box>
              <Box>
                <Typography sx={{
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  mb: 0.3
                }}>
                  Full Loan Repayment
                </Typography>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '11px',
                  lineHeight: '1.3'
                }}>
                  Pay off entire balance now
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={isFullRepayment}
              onChange={handleSwitch}
              onClick={(e) => e.stopPropagation()}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4A7AF2',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  bgcolor: '#4A7AF2',
                },
                '& .MuiSwitch-track': {
                  bgcolor: 'rgba(255,255,255,0.15)',
                },
              }}
            />
          </Box>
        </Box>

        {/* Installment Payment Section */}
        {!isFullRepayment ? (
          <Box mb={2} mt={2}>
            {/* Installment Selector Card */}
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.02)',
              borderRadius: '8px',
              p: 2,
              mb: 2,
              border: '1px solid rgba(255,255,255,0.06)'
            }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="body1" sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '14px'
                }}>
                  Installments
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: '30px',
                      fontWeight: 700,
                      fontSize: '18px',
                      color: '#fff',
                      textTransform: 'none',
                    }}
                    disabled={selectedInstallments <= 1}
                    onClick={() => setSelectedInstallments(selectedInstallments - 1)}
                  >
                    -
                  </Button>

                  <Typography
                    variant="h6"
                    sx={{ minWidth: 32, textAlign: 'center', color: '#fff' }}
                  >
                    {selectedInstallments}
                  </Typography>

                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: '30px',
                      fontWeight: 700,
                      fontSize: '18px',
                      color: '#fff',
                      textTransform: 'none',
                    }}
                    disabled={selectedInstallments >= remainingPayableInstallments}
                    onClick={() => setSelectedInstallments(selectedInstallments + 1)}
                  >
                    +
                  </Button>
                </Box>
              </Box>

              {/* Payment Amount */}
              <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pt: 1.5,
                borderTop: '1px solid rgba(255,255,255,0.08)'
              }}>
                <Typography variant="body1" sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '14px'
                }}>
                  Payment Amount
                </Typography>
                <Typography variant="h6" sx={{
                  color: '#4EDF6D',
                  fontWeight: 700,
                  fontSize: '18px'
                }}>
                  {boosterLoanDetails?.installments[0]?.amount
                    ? (formatter.balance(boosterLoanDetails.installments[0].amount * selectedInstallments)).display
                    : '0.00'}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box mb={2} mt={2}>
            {isLoadingAmount ? (
              <Typography className="text-secondary" textAlign="center" py={2}>
                Loading...
              </Typography>
            ) : (
              <Box>
                {/* Amount Calculation Section */}
                <Box sx={{
                  bgcolor: 'rgba(255,255,255,0.02)',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2.5,
                  border: '1px solid rgba(255,255,255,0.06)'
                }}>
                  {/* Step 1: Remaining Loan Balance */}
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.08)'
                  }}>
                    <Typography variant="body1" sx={{
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '14px'
                    }}>
                      Remaining Amount
                    </Typography>
                    <Typography variant="h6" sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 700,
                      fontSize: '18px'
                    }}>
                      {remainingAmount !== null ? formatter.balance(remainingAmount).display : '0.00'}
                    </Typography>
                  </Box>

                  {/* Step 2: Saving Amount */}
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <Typography variant="body1" sx={{
                      color: (savingAmount !== null && savingAmount < 0) ? '#F2674A' : 'rgba(255,255,255,0.6)',
                      fontSize: '14px'
                    }}>
                      {(savingAmount !== null && savingAmount < 0) ? 'Additional Amount' : 'Saving Amount'}
                    </Typography>
                    <Typography variant="h6" sx={{
                      color: (savingAmount !== null && savingAmount < 0) ? '#F2674A' : 'rgba(255,255,255,0.9)',
                      fontWeight: 700,
                      fontSize: '18px'
                    }}>
                      {savingAmount !== null
                        ? `${savingAmount < 0 ? '+' : '-'}${formatter.balance(Math.abs(savingAmount)).display}`
                        : '0.00'}
                    </Typography>
                  </Box>
                </Box>

                {/* Step 3: Total Payoff Amount with Breakdown */}
                <Box sx={{
                  bgcolor: 'rgba(78, 223, 109, 0.06)',
                  borderRadius: '8px',
                  p: 2,
                  mb: 1,
                  border: '1px solid rgba(78, 223, 109, 0.15)'
                }}>
                  {/* Total Amount Header */}
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    pb: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <Typography variant="body1" sx={{
                      fontWeight: 700,
                      color: '#fff',
                      fontSize: '15px'
                    }}>
                      Total Payoff Amount
                    </Typography>
                    <Typography variant="h5" sx={{
                      color: '#4EDF6D',
                      fontWeight: 700,
                      fontSize: '22px'
                    }}>
                      {formatter.balance(payableAmount).display}
                    </Typography>
                  </Box>



                  {/* Principle Amount */}
                  {paymentBreakdown.principle !== null && (
                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5
                    }}>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '13px'
                      }}>
                        Principle Amount
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        {formatter.balance(paymentBreakdown.principle).display}
                      </Typography>
                    </Box>
                  )}

                  {/* Interest/Service Fees */}
                  {paymentBreakdown.interestFees !== null && (
                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5
                    }}>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '13px'
                      }}>
                        Interest/Service Fees
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        {formatter.balance(paymentBreakdown.interestFees).display}
                      </Typography>
                    </Box>
                  )}

                  {/* Origination Fee */}
                  {paymentBreakdown.originationFee !== null && (
                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5
                    }}>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '13px'
                      }}>
                        Origination Fee
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        {formatter.balance(paymentBreakdown.originationFee).display}
                      </Typography>
                    </Box>
                  )}

                  {/* Prepayment Fee */}
                  {paymentBreakdown.prepaymentFee !== null && (
                    <Box sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '13px'
                      }}>
                        Prepayment Fee
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 500,
                        fontSize: '13px'
                      }}>
                        {formatter.balance(paymentBreakdown.prepaymentFee).display}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Payment method display and change */}
        <Box mb={2}>
          <IconButton
            onClick={() => setShowPaymentMethodDialog(true)}
            sx={{ width: '100%', borderRadius: 2, p: 1, paddingLeft: '10px', paddingRight: '10px', background: 'linear-gradient(45deg, #4b2c20, #5e4028)' }}
          >
            <Box width="100%" display="flex" alignItems="center">
              <CreditCardIcon fontSize="small" sx={{ color: '#ffbf00', mr: 1 }} />
              <Typography variant="body2" className="text-white">Payment Method</Typography>
              <Box flexGrow={1} />
              {(() => {
                const icon = renderPaymentIcon(method, 0);
                const detail = method?.type === 'shareSellDown' || method?.type === 'bankAccount'
                  ? method?.displayName
                  : `${method?.displayName} ${method?.displayNumber}`;
                return <>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}> {icon}</Box>
                  <Typography variant="body2" sx={{ ml: 1 }} className="text-white">{detail || 'No method'}</Typography></>;
              })()}
            </Box>
          </IconButton>
        </Box>

        {/* Pay Now button */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            sx={{
              mt: 2,
              minWidth: 160,
              borderRadius: '30px',
              fontWeight: 700,
              fontSize: '18px',
              background: 'linear-gradient(90deg, #f2674a 0%, #ffbf00 100%)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(242, 103, 74, 0.15)',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #d55a3f 0%, #ffbf00 100%)',
                boxShadow: '0 4px 16px rgba(242, 103, 74, 0.25)',
              },
            }}
            disabled={isLoadingAmount || loading}
            onClick={handlePayNow}
          >
            Pay Now
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BoosterLoanPaymentDialog;


